/**
 * The panel arithmetic.
 *
 * This file exists so that the one number the feature turns on — how many
 * independent passes agreed — is computed in exactly one place, from the stored
 * votes, deterministically. Nothing here calls a model, and nothing here is
 * stored: a derived number written to disk is a number that can disagree with
 * the votes it came from.
 *
 * No model is ever asked how much the panel agreed. That question has an exact
 * answer and asking for it would replace a count with a fourth opinion.
 */

import type { Lens, ModelStamp, PanelCheck, PanelVerdict, PanelVote } from './types'
// The one thing shared with the calibration ledger, and deliberately the only
// one: two copies of "which stamps are the same model" is two surfaces that can
// disagree about it (D13). Nothing else crosses — the numbers below are counts of
// verdicts and are never averaged with a Brier score.
import { modelKey } from './calibration'
import { PANEL_CHIP_LABEL, PANEL_VERDICT_LABEL } from './vocabulary'

export { PANEL_CHIP_LABEL, PANEL_VERDICT_LABEL }

/**
 * Seats on the panel. Three, and the argument is arithmetic rather than taste.
 *
 * At two, a 1–1 split is uninterpretable. Three is the smallest N where
 * consensus (3–0), a majority with a nameable dissent (2–1) and total
 * unsettledness (1–1–1) are three DISTINGUISHABLE states rather than one
 * boolean. Four buys a 2–2 tie for a third more money and a new ambiguity. Cost
 * is linear in seats and every seat is a high-effort reasoning pass.
 */
export const PANEL_SIZE = 3

export const isDecided = (v: PanelVerdict) => v !== 'undecidable'

/**
 * How many decided verdicts it takes before anything here may be called agreement.
 *
 * Two, and the words carry the argument: agreement, convergence and unanimity are
 * all facts about a PAIR. One decided verdict has nothing to agree WITH — and
 * every predicate spelled "they landed in the same place" comes out true on it
 * anyway, because `agreed === decided` is 1 === 1 and `split === 0` for want of a
 * second position to differ from. So this floor is not a refinement of those
 * tests. It is the half of them the arithmetic cannot express, and it has to be
 * asked for by name at every site that turns a count into a claim.
 *
 * One definition, read by all four of them: `panelStability`'s first arm, which
 * decides whether a stored panel is counted as the model agreeing with itself;
 * `objectionToLand`, which decides whether a converged `fails` puts a node on the
 * graph; `panelChip`, which decides whether a chip may say the lenses agreed; and
 * `panelSummary`, which decides whether the SENTENCE may. They arrived one round
 * apart each: the first got the floor as a bare `2`, the second was missed for a
 * round, the third for one more, and the fourth was recorded as needing nothing at
 * all — because it discloses the thin panel in its first clause, which is true, and
 * then said "all 1 agreed" in its second, which is the word. One rule at several
 * sites where only some of them got stricter is exactly the shape D13 is about, so
 * the value stopped being a literal the moment there was a second reader for it.
 *
 * A LOST SEAT does not raise the bar. The stricter `returned === asked` was
 * weighed and refused where `ModelStability.unanimous` records why; this floor
 * asks only that a pair of verdicts EXIST, so a genuine 2–0 with the third seat
 * errored or abstained clears it at both sites.
 */
export const DECIDED_PAIR_FLOOR = 2

/**
 * Did the passes that took a position disagree? Allocation-free, on purpose.
 *
 * `channelOf` is not on the frame path but this predicate IS: the renderer reads
 * it per strand per frame, and the label overlay reads it per node per frame. A
 * version that allocated a Map and a filtered array is tens of thousands of
 * allocations a second on a large graph, arriving exactly when the layout is
 * already forcing every edge geometry to rebuild.
 *
 * Two rules do all the work, and they are the same two `tallyPanel` uses:
 *
 *  1. Agreement is EXACT EQUALITY, with no ordinal collapsing. `holds` and
 *     `holds-weaker` are a split, because the difference between "it holds" and
 *     "it holds only under a condition the claim never stated" is precisely the
 *     unsettledness this feature exists to surface.
 *  2. An `undecidable` vote is a pass that RAN but never a pass that DISSENTED.
 *     "Nobody can compute this" is an admission, and this graph already has a
 *     channel for admissions; counting it as dissent would collapse a split into
 *     a gap and make both mean less.
 */
export function panelSplit(p: PanelCheck | undefined): boolean {
  if (!p) return false
  let first: PanelVerdict | null = null
  for (const v of p.votes) {
    if (!isDecided(v.verdict)) continue
    if (first === null) first = v.verdict
    else if (v.verdict !== first) return true
  }
  return false
}

export interface PanelTally {
  asked: number
  returned: number
  decided: number
  /** Passes that ran and named a quantity they would need. NEVER dissent. */
  abstained: number
  /** The largest group of identical verdicts among the decided passes. */
  agreed: number
  /** `decided − agreed`. One or more is exactly what a split means. */
  split: number
  /** The verdict of the strictly-largest group, or null when there isn't one —
   *  three lenses and three different verdicts have no majority to name. */
  majority: PanelVerdict | null
}

/** The count, for the inspector and the export. ALLOCATES — a Map and, since the
 *  loop moved, the verdict array it reads — so never call this below
 *  `Renderer.draw()` or inside the label overlay's per-frame pass. */
export function tallyPanel(p: PanelCheck): PanelTally {
  return tallyVerdicts(
    p.lenses.length,
    p.votes.map((v) => v.verdict),
  )
}

/**
 * The same count, from the two things it actually reads.
 *
 * Split out for `panelStability`, which counts agreement across STORED panels and
 * holds their verdicts without their prose. The alternative was a second copy of
 * this loop, and the whole reason this file exists is that the number the feature
 * turns on is computed in exactly one place. Reconstructing votes to satisfy the
 * old signature would have been worse still: fabricating a `because` and a
 * `confidence` nothing reads is inventing record fields.
 */
export function tallyVerdicts(asked: number, verdicts: readonly PanelVerdict[]): PanelTally {
  const counts = new Map<PanelVerdict, number>()
  let decided = 0
  let abstained = 0
  for (const verdict of verdicts) {
    if (!isDecided(verdict)) {
      abstained++
      continue
    }
    decided++
    counts.set(verdict, (counts.get(verdict) ?? 0) + 1)
  }
  let agreed = 0
  let majority: PanelVerdict | null = null
  let ties = 0
  for (const [verdict, n] of counts) {
    if (n > agreed) {
      agreed = n
      majority = verdict
      ties = 1
    } else if (n === agreed) {
      ties++
    }
  }
  return {
    asked,
    returned: verdicts.length,
    decided,
    abstained,
    agreed,
    split: Math.max(0, decided - agreed),
    // Strictly largest, or nothing. A 1–1–1 panel has no majority, and naming one
    // of the three would invent a consensus out of a three-way disagreement.
    majority: ties === 1 ? majority : null,
  }
}

/** How far a verdict sits from another, for picking the sharpest dissent. Ordinal
 *  here and only here: `panelSplit` must stay exact-equality, but "which of two
 *  dissents is further from the majority" is a distance question. */
const RANK: Record<PanelVerdict, number> = {
  holds: 0,
  'holds-weaker': 1,
  fails: 2,
  undecidable: 1,
}

/**
 * The dissenting pass furthest from where the rest landed.
 *
 * Ties break on the pass's own confidence, then on the order the passes ran, so
 * one record always names the same dissent on every reload and in every export.
 */
export function sharpestDissent(p: PanelCheck): PanelVote | null {
  const t = tallyPanel(p)
  if (!t.majority || t.split === 0) return null
  const base = RANK[t.majority]
  let best: PanelVote | null = null
  let bestD = -1
  for (const v of p.votes) {
    if (!isDecided(v.verdict) || v.verdict === t.majority) continue
    const d = Math.abs(RANK[v.verdict] - base)
    if (d > bestD || (d === bestD && best && v.confidence > best.confidence)) {
      best = v
      bestD = d
    }
  }
  return best
}

/**
 * Which pass's objection lands on the graph as a node, if any. At most one.
 *
 * With a split it is the sharpest dissent, but only when that dissent is
 * `fails`: a `holds-weaker` dissent is a condition, and a condition is an
 * annotation rather than a move in the argument.
 *
 * With a panel that CONVERGED on `fails` there is no dissent at all — and that is
 * the most valuable thing this op can hand back, because a contradiction three
 * disciplines reached independently is stronger than any one of them. So the node
 * comes from the most confident of the agreeing objections.
 *
 * Which is why that branch reads `DECIDED_PAIR_FLOOR`: convergence needs a pair to
 * converge. One seat returning `fails` while the other two error satisfies
 * `majority === 'fails'` and `split === 0` — not because the lenses landed
 * together but because there was nothing for the one verdict to differ from — and
 * it used to land that lone objection as a `contradiction` node, which is the
 * loudest thing this op can produce: `kindChannelOf` puts it on the tension
 * channel, `openness` in `runPolicy.ts` scores that channel highest, and an
 * autonomous run then steers into the subtree one lens created. The panel is
 * STORED either way and the vote keeps its place in every count and in
 * `panelSummary`'s sentence, because the record is the record. What the floor
 * withholds is the graph mutation, which is a claim about how many lenses got
 * there. The split branch needs no guard of its own: a split IS two decided
 * verdicts.
 */
export function objectionToLand(p: PanelCheck): PanelVote | null {
  const t = tallyPanel(p)
  if (t.majority === 'fails' && t.split === 0 && t.decided >= DECIDED_PAIR_FLOOR) {
    let best: PanelVote | null = null
    for (const v of p.votes) {
      if (v.verdict !== 'fails') continue
      if (!best || v.confidence > best.confidence) best = v
    }
    return best
  }
  const d = sharpestDissent(p)
  return d && d.verdict === 'fails' ? d : null
}

/**
 * Who sits on the panel.
 *
 * The session's own lens always sits: a panel that omitted the frame the node was
 * derived under could not tell you whether the derivation survives. The other two
 * are hand-picked per lens rather than rotated, because "which two disciplines
 * would break this one on DIFFERENT grounds" is a judgement, and a readable table
 * is auditable where an algorithm is not.
 *
 * `adversarial` is never ADDED. Its brief instructs the model to assume the idea
 * is wrong and find the fastest route to disproof, so a panel containing it would
 * dissent on nearly every node — and a marking that fires on everything encodes
 * nothing. It sits only when the user chose it for the session. This is the most
 * load-bearing line in the feature.
 */
export const PANEL_COMPANIONS: Record<Lens, [Lens, Lens]> = {
  'first-principles': ['empirical', 'systems'],
  technical: ['first-principles', 'economic'],
  empirical: ['first-principles', 'systems'],
  economic: ['technical', 'systems'],
  adversarial: ['first-principles', 'empirical'],
  systems: ['first-principles', 'empirical'],
  historical: ['first-principles', 'economic'],
}

export const panelLenses = (l: Lens): Lens[] => [l, ...PANEL_COMPANIONS[l]]

/**
 * How many BILLED REQUESTS one panel makes — the session's lens and its two
 * companions, so three, for every lens in the table above.
 *
 * Exported because the money layer needs it and must not guess it. `runPanel`
 * runs all three seats inside one op with no stop condition between them, so a
 * panel is three passes behind one gate: a reserve holding room for one of them
 * admits a panel it cannot cover. `run.ts` reads this rather than a literal 3,
 * so widening or narrowing the companion table moves the reserve with it.
 *
 * DERIVED from the table rather than written beside it. The type says two
 * companions and the table obeys, but a constant asserting three would go on
 * asserting it after either changed — and it would be the reserve, the one
 * number that must not quietly stop being true.
 */
export const PANEL_SEATS: number = panelLenses('first-principles').length

/**
 * Which seats never came back, BY NAME.
 *
 * Membership, and it has to be: `votes` holds the passes that returned, in the
 * order they returned, and a seat that failed is simply absent from it — so the
 * count `votes.length` says how many arrived and nothing whatever about which.
 * Reading the tail of `lenses` off that count is right only when the seats that
 * failed were the last ones asked. With the FIRST seat lost, `votes[0]` carries
 * the second lens, the tail names the third — a lens that answered and is already
 * printed above with its verdict — and the one that actually failed is never named
 * anywhere. A display that lists a returned lens under "did not return" is worse
 * than one that stays quiet, because it is a specific false statement about which
 * discipline went unheard.
 *
 * Ask order, because that is the order the seats ran in and the order every
 * surface lists them in. Both surfaces that name a lost seat — the sheet's rows
 * and the exported document — take it from here, so the screen and the file
 * cannot name different seats for the same panel.
 */
export function missingLenses(p: PanelCheck): Lens[] {
  const returned = new Set(p.votes.map((v) => v.lens))
  return p.lenses.filter((l) => !returned.has(l))
}

// `PANEL_VERDICT_LABEL` — what one seat ruled — and `PANEL_CHIP_LABEL` — what the
// panel as a whole reads as — are status words, so they are defined in
// core/vocabulary.ts beside every other one and re-exported from this module,
// which still decides which of them a panel has earned.

/**
 * The clause that travels with every count of agreement in this app.
 *
 * A constant rather than a phrase retyped per surface, for the reason the panel
 * exists at all: three passes of one model converging is one model converging, and
 * the sentence saying so must not be able to drift, weaken or go missing on the
 * surface that most needs it. `panelSummary` says it about one panel;
 * `stabilityLine` says it about hundreds. Same words.
 */
export const AGREEMENT_CAVEAT = 'Agreement between passes is agreement, not correctness.'

/**
 * The one-line reading of a panel, in words.
 *
 * Everything the canvas says has to be sayable in prose somewhere, and this is
 * the sentence the split strand means. Note what it does not say: agreement is
 * reported as agreement, never as correctness.
 *
 * The FOURTH reader of `DECIDED_PAIR_FLOOR`, and the last to get it. This sentence
 * used to reach the convergence branch with one decided verdict and print "all 1
 * agreed" — `split === 0` is true for want of a second position to differ from, so
 * the branch that reports convergence caught every panel the floor exists to keep
 * that word off, and the abstained wording said "the 1 that took a position agreed"
 * on the same panels. It travels: `runPanel`'s completion toast, the grounding
 * sheet, and both exported documents print exactly this string.
 *
 * Under the floor it states the verdict and then names what was missing, in the
 * clause `stabilityLine` ends on and `panelChip`'s third state is worded off,
 * because all three are the same absence at three sizes. That branch carries NO
 * `AGREEMENT_CAVEAT`, and the omission is the point: the clause qualifies a claim of
 * agreement, this branch deliberately makes none, and appending it would put the
 * word back into the one sentence that just avoided it.
 */
export function panelSummary(p: PanelCheck): string {
  const t = tallyPanel(p)
  if (t.returned === 0) return 'No pass returned a verdict.'
  const seats = `${t.returned} of ${t.asked} lens${t.asked === 1 ? '' : 'es'} answered`
  if (t.decided === 0) return `${seats}, and none of them could reach the claim.`
  // Above `split === 0`, and reachable only with at least one decided verdict since
  // the arm above returns on none — so on three seats this is exactly one, and
  // `majority` is that verdict rather than null. `seats` already tells the two
  // shapes apart: "1 of 3 lenses answered" is two seats lost, "3 of 3" is two
  // abstentions, and the clause after it reads the same way for both.
  if (t.decided < DECIDED_PAIR_FLOOR) {
    return `${seats}; the one that decided said it ${PANEL_VERDICT_LABEL[t.majority!].toLowerCase()} — fewer than two decided verdicts to compare.`
  }
  if (t.split === 0) {
    const all = t.abstained
      ? `the ${t.decided} that took a position agreed`
      : `all ${t.decided} agreed`
    return `${seats}; ${all} that it ${PANEL_VERDICT_LABEL[t.majority!].toLowerCase()}. ${AGREEMENT_CAVEAT}`
  }
  if (!t.majority) {
    return `${seats}, and the ${t.decided} that took a position landed in ${t.decided} different places. Nothing here is settled.`
  }
  return `${seats}; ${t.agreed} said it ${PANEL_VERDICT_LABEL[t.majority].toLowerCase()} and ${t.split} disagreed. An unsettled question, not a resolved one.`
}

/**
 * What a chip may say about a panel in three words. Three states, because two of
 * them were being carried by one.
 *
 * The chip read `panelSplit(p) ? disagreed : agreed`, and `panelSplit` is false on
 * a panel with nothing to compare: one decided verdict has no second position to
 * differ from, and no decided verdict has none at all. So a panel where two seats
 * errored and the third said `holds` was captioned "The lenses agreed" — the
 * absence of disagreement printed as positive agreement, in the shortest and
 * loudest sentence on the sheet, on exactly the panels `DECIDED_PAIR_FLOOR` exists
 * to keep that word off. This is the third site that turns a count into a claim,
 * and like the other two it asks for the floor by name rather than re-deriving it.
 * The review that found the second site saw this one and left it, because a third
 * chip state is a design decision where the other two were one floor read in two
 * places; D34's third amendment is where that decision is recorded.
 *
 * The third state names what was missing instead of hedging the second, and it is
 * worded off `stabilityLine`'s last clause — "fewer than two decided verdicts to
 * compare" — because they are the same absence at two sizes. A hedge on the second
 * state ("the lenses agreed, but…") would have kept the word and asked the reader
 * to discount it, which is how the sentence got here.
 *
 * A LOST SEAT does not demote a genuine pair. `returned === asked` was weighed and
 * refused where `ModelStability.unanimous` records why — a lost seat is a transport
 * failure and a failure is not dissent — so a 2–0 with the third seat errored or
 * abstained reads `agreed` here exactly as it lands there and counts there.
 *
 * `panelSummary` was recorded here as untouched by any of this and needing nothing,
 * and one clause of it was: it opens "1 of 3 lenses answered", which discloses the
 * thin panel the chip was overstating. The next clause said "all 1 agreed" — which
 * is not a reading of the record but a word put on it, on exactly these panels, in
 * the sentence that goes to the completion toast, the grounding sheet and both
 * exports. So the floor has a fourth reader now and it sits above `split === 0`
 * there. A sentence disclosing its own thinness in one clause and overstating it in
 * the next is not half right; the second clause is the one a reader repeats.
 *
 * The CANVAS is deliberately not part of this and must not grow a third strand.
 * Divergence there is FORM — one strand drawn as two that never rejoin, read from
 * `panelSplit` — and form encodes divergence ONLY, so a strand with no split mark
 * asserts nothing about agreement and has nothing to correct. `types.ts` says so
 * where `Channel` is declared. The chip was the problem because a chip has words.
 */
export type PanelChip = 'disagreed' | 'agreed' | 'too-few-decided'

// The chip's words are `PANEL_CHIP_LABEL`, in core/vocabulary.ts: never in the
// component, because a label edited on a surface is a claim edited without its
// rule — and never beside the arithmetic alone either, because the chip is one
// of the app's status words and the one table is how they stay one set.

/** Which of the three a panel is, argued at `PanelChip` above. ALLOCATES, through
 *  `tallyPanel` — a sheet reads this once when it opens and nothing below
 *  `Renderer.draw()` reads it at all, which is why `panelSplit` stays the
 *  allocation-free one and this may call the count. */
export function panelChip(p: PanelCheck): PanelChip {
  if (panelSplit(p)) return 'disagreed'
  return tallyPanel(p).decided >= DECIDED_PAIR_FLOOR ? 'agreed' : 'too-few-decided'
}

/* ── how steady one model's own reasoning has been ───────────────────── */

/**
 * One stored panel, reduced to what a count of agreement can be taken from.
 *
 * Structural rather than a named record type, on `CalibrationRow`'s precedent:
 * `PanelRecord` in `persist.ts` satisfies it, and nothing in this file imports
 * storage to say so.
 */
export interface PanelRun {
  sessionId: string
  /** The model all three seats ran on, resolved once before seat one. Singular by
   *  construction — see `PanelCheck.model`, which is why this surface can name a
   *  model at all. */
  model: ModelStamp
  /** How many lenses were asked. Longer than `verdicts` when a pass failed. */
  asked: number
  /** Where each pass that returned landed, and nothing else off the vote. */
  verdicts: PanelVerdict[]
  checkedAt: number
}

/**
 * One model's record of agreeing with itself.
 *
 * The four states are mutually exclusive and sum to `panels`, so the row is
 * checkable by addition rather than by trust — which is the same discipline the
 * calibration bands print numerators for.
 */
export interface ModelStability {
  model: ModelStamp
  key: string
  explorations: number
  panels: number
  /** Every pass that took a position took the same one AND there were at least
   *  `DECIDED_PAIR_FLOOR` of them: `agreed === decided` alone is satisfied by a
   *  single decided verdict, and a row headed "how often it agreed with itself"
   *  then reported agreement on a panel where no PAIR of passes had agreed. Why
   *  the floor exists is stated once, at the constant; what it does here is carry
   *  the word. An abstention is still not a dissent, here as in `panelSplit`, so a
   *  2–0 with one abstention is unanimous.
   *
   *  A LOST SEAT does not demote it either, and the stricter `returned === asked`
   *  was weighed and refused: agreement is a fact about the passes that RETURNED, a
   *  lost seat is already visible in the stored tally as `asked` against `returned`,
   *  and demoting a genuine 2–0 because a third seat errored would report a
   *  transport failure as an epistemic one — the exact confusion this layer exists
   *  to prevent, since a failure is not dissent. The pair floor is what makes
   *  "unanimous" mean a pair agreed; nothing else on this row needs to. */
  unanimous: number
  /** A strict majority and at least one pass somewhere else. */
  dissent: number
  /** No strictly-largest group: a 1–1–1, or a 1–1 with one abstention. */
  noMajority: number
  /** Under `DECIDED_PAIR_FLOOR` — the panel came back without a pair to compare.
   *  Either no pass could reach the claim, or exactly one did, which is a pass that
   *  reached it and decided with nothing to agree WITH. Both are the absence of a
   *  comparison rather than a reading, which is why they share a bucket. Named for
   *  panels: `PanelTally.abstained` counts passes. */
  tooFewDecided: number
}

/**
 * The same count, across every panel the LIBRARY holds. One row per model that
 * has been paneled, busiest first.
 *
 * The library and not the device: `loadPanelRecords` walks the localStorage
 * index, so panels inside an exploration the budget moved to the archive are not
 * counted until it is opened again. `stabilityLine` says "in the library" for
 * that reason and not as a turn of phrase.
 *
 * A DIFFERENT QUESTION from the calibration ledger, and the two must never be
 * averaged or shown as one figure. Calibration asks whether a model's confidence
 * matched what the world did; this asks how often three blind passes of one model
 * landed in the same place. A model can be perfectly stable and perfectly wrong —
 * that is exactly what three passes converging on a mistake looks like — so a
 * number mixing them would answer neither question. Per model and never pooled,
 * for the reason the ledger is: a figure spanning two models would be counting two
 * different things agreeing with themselves.
 *
 * Two walls hold it up, and both are older than it is:
 *
 *  - **Nothing here is stored.** This file's header rules that a derived number
 *    written to disk is a number that can disagree with the votes it came from,
 *    so these counts are recomputed from the stored documents on every open, the
 *    way the two calibration ledgers are.
 *  - **Verdicts are counted; self-ratings are never pooled.** D24 declined the
 *    `echo` cue because its firing rule pooled self-ratings across a confidence
 *    spread. That defect is not in this: `PanelVote.confidence` is not read here
 *    at all, and a verdict is a POSITION a pass took, not a number it gave itself.
 *    Counting how often positions coincided is arithmetic over records; averaging
 *    what three passes thought of themselves is a fourth opinion.
 *
 * Nothing in here calls a model, for the reason the top of this file gives: how
 * much the passes agreed has an exact answer.
 */
export function panelStability(records: readonly PanelRun[]): ModelStability[] {
  const groups = new Map<string, PanelRun[]>()
  for (const r of records) {
    const k = modelKey(r.model)
    const g = groups.get(k)
    if (g) g.push(r)
    else groups.set(k, [r])
  }

  const out: ModelStability[] = []
  for (const [key, rs] of groups) {
    let unanimous = 0
    let dissent = 0
    let noMajority = 0
    let tooFewDecided = 0
    for (const r of rs) {
      const t = tallyVerdicts(r.asked, r.verdicts)
      // The four arms are ordered so that each one's condition is read against
      // what the ones above it already took: nothing can fall through, and
      // nothing can be counted twice. The pair floor sits in the FIRST arm, which
      // is what lets the second stay `agreed === decided`: below the first arm
      // there are always two or more decided verdicts for that equality to be
      // about, so it can no longer come out true on a panel of one.
      if (t.decided < DECIDED_PAIR_FLOOR) tooFewDecided++
      else if (t.agreed === t.decided) unanimous++
      else if (t.majority) dissent++
      else noMajority++
    }
    // The newest stamp wins the display name, as in `calibrate`: a model keeps
    // its id across a rename and the row should print what it is called now.
    const newest = rs.reduce((a, b) => (b.checkedAt > a.checkedAt ? b : a))
    out.push({
      model: newest.model,
      key,
      explorations: new Set(rs.map((r) => r.sessionId)).size,
      panels: rs.length,
      unanimous,
      dissent,
      noMajority,
      tooFewDecided,
    })
  }

  return out.sort((a, b) => b.panels - a.panels || b.unanimous - a.unanimous)
}

/**
 * The one-line reading of a model's stability, in words.
 *
 * All four counts always print, including the zeroes: they sum to `panels`, and a
 * line that dropped its empty terms would stop being checkable by addition. Every
 * number the row holds is in this one sentence, so nothing may be appended after
 * it — it ends where every count of agreement in this app ends, and a caller that
 * added a clause of its own would put something between the reader and the one
 * sentence standing between a high unanimity rate and a reading of correctness.
 *
 * "In the library" rather than "on this device", because the counts are only ever
 * the library's: the archive's documents are behind an await and this line is
 * printed in a render. There is no room here to say what the archive is or where
 * it went — the sentence must end on the caveat — so the surfaces around it carry
 * that, and this one simply stops claiming more than it counted.
 *
 * The last clause names the threshold instead of claiming nobody reached the
 * claim. It read "that no pass could reach", which is false of half of what that
 * bucket now holds: with one decided verdict a pass DID reach it and decide, and
 * what was missing was a second verdict to compare that one against. So the
 * sentence says the thing that was actually missing.
 */
export function stabilityLine(s: ModelStability): string {
  const where = `${s.panels} panel${s.panels === 1 ? '' : 's'} across ${s.explorations} exploration${s.explorations === 1 ? '' : 's'} in the library`
  return `${where}: ${s.unanimous} unanimous, ${s.dissent} with a dissent, ${s.noMajority} with no majority, and ${s.tooFewDecided} with fewer than two decided verdicts to compare. ${AGREEMENT_CAVEAT}`
}
